/**
 * 全域案件狀態 Context
 * 支援兩種案件類型：PDD（專案設計文件）/ nmeth（方法論申請文件）
 * header 選擇案件類型 + 案件，所有頁面共用
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import projectsService from '../services/projectsService';
import nmethProposalService, { NmethProposalDetail } from '../services/nmethProposalService';
import { Project } from '../types/projects';
import { useAuth } from './AuthContext';

export type CaseType = 'pdd' | 'nmeth';

interface ProjectContextType {
  // 案件類型
  caseType: CaseType;
  setCaseType: (type: CaseType) => void;

  // PDD 專案
  projects: Project[];
  selectedProject: Project | null;
  selectedProjectId: number | null;
  setSelectedProjectId: (id: number | null) => void;

  // 方法論提案
  proposals: NmethProposalDetail[];
  selectedProposal: NmethProposalDetail | null;
  selectedProposalId: number | null;
  setSelectedProposalId: (id: number | null) => void;

  loading: boolean;
  reloadProjects: () => Promise<void>;
  reloadProposals: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType>({
  caseType: 'pdd',
  setCaseType: () => {},
  projects: [],
  selectedProject: null,
  selectedProjectId: null,
  setSelectedProjectId: () => {},
  proposals: [],
  selectedProposal: null,
  selectedProposalId: null,
  setSelectedProposalId: () => {},
  loading: false,
  reloadProjects: async () => {},
  reloadProposals: async () => {},
});

const STORAGE_CASE_TYPE = 'selected_case_type';
const STORAGE_PROJECT_ID = 'selected_project_id';
const STORAGE_PROPOSAL_ID = 'selected_proposal_id';

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // 案件類型
  const [caseType, setCaseTypeState] = useState<CaseType>(() => {
    const saved = localStorage.getItem(STORAGE_CASE_TYPE);
    return (saved === 'nmeth') ? 'nmeth' : 'pdd';
  });

  // PDD 專案
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem(STORAGE_PROJECT_ID);
    return saved ? Number(saved) : null;
  });

  // 方法論提案
  const [proposals, setProposals] = useState<NmethProposalDetail[]>([]);
  const [selectedProposalId, setSelectedProposalIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem(STORAGE_PROPOSAL_ID);
    return saved ? Number(saved) : null;
  });

  const [loading, setLoading] = useState(false);

  // ========== 載入專案清單 ==========
  const reloadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await projectsService.getAll();
      const filtered = data.filter((p: Project) => p.pdd_status === 229);
      setProjects(filtered);
    } catch (err) {
      console.error('[ProjectContext] Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ========== 載入方法論提案清單 ==========
  const reloadProposals = useCallback(async () => {
    try {
      setLoading(true);
      const data = await nmethProposalService.getList();
      // 僅顯示草稿/修訂狀態 (status = 258)
      const filtered = data.filter((p: NmethProposalDetail) => p.status === 258);
      setProposals(filtered);
    } catch (err) {
      console.error('[ProjectContext] Failed to load proposals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ========== 登入/組織變更時載入 ==========
  useEffect(() => {
    if (isAuthenticated && user) {
      reloadProjects();
      reloadProposals();
      // 組織切換時清除已選
      setSelectedProjectId(null);
      setSelectedProposalId(null);
    } else {
      setProjects([]);
      setProposals([]);
      setSelectedProjectIdState(null);
      setSelectedProposalIdState(null);
      localStorage.removeItem(STORAGE_PROJECT_ID);
      localStorage.removeItem(STORAGE_PROPOSAL_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.organization_id]);

  // ========== 設定案件類型 ==========
  const setCaseType = (type: CaseType) => {
    setCaseTypeState(type);
    localStorage.setItem(STORAGE_CASE_TYPE, type);
  };

  // ========== 設定已選專案 ==========
  const setSelectedProjectId = (id: number | null) => {
    setSelectedProjectIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_PROJECT_ID, String(id));
    } else {
      localStorage.removeItem(STORAGE_PROJECT_ID);
    }
  };

  // ========== 設定已選方法論提案 ==========
  const setSelectedProposalId = (id: number | null) => {
    setSelectedProposalIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_PROPOSAL_ID, String(id));
    } else {
      localStorage.removeItem(STORAGE_PROPOSAL_ID);
    }
  };

  // ========== 計算已選物件 ==========
  const selectedProject = selectedProjectId
    ? (projects.find(p => p.id === selectedProjectId) || null)
    : null;

  const selectedProposal = selectedProposalId
    ? (proposals.find(p => p.id === selectedProposalId) || null)
    : null;

  return (
    <ProjectContext.Provider value={{
      caseType,
      setCaseType,
      projects,
      selectedProject,
      selectedProjectId,
      setSelectedProjectId,
      proposals,
      selectedProposal,
      selectedProposalId,
      setSelectedProposalId,
      loading,
      reloadProjects,
      reloadProposals,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => useContext(ProjectContext);
